import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useSupabaseSession } from "@/hooks/use-supabase-session";
import { useToast } from "@/hooks/use-toast";
import { sendProgressEvent } from "@/lib/progress";
import { cn } from "@/lib/utils";
import {
  createCircleTaskInDb,
  deleteCircleTaskInDb,
  fetchCircleById,
  listCircleTasksForMe,
  toggleCircleTaskCompletionInDb,
  updateCircleTaskInDb,
  type CircleTaskKind,
} from "@/lib/supabase/circles";

type MeditationSession = {
  id: string;
  title: string;
  description: string;
  durationMinutes: number;
  level: "beginner" | "intermediate" | "advanced";
  coverImageUrl?: string;
  audioUrl?: string;
  active: boolean;
  tags: string[];
};

type MeditationsResponse = {
  ok: true;
  items: MeditationSession[];
};

type WellnessActivity = {
  id: string;
  title: string;
  description: string;
  durationMinutes?: number;
  active: boolean;
  tags: string[];
};

type ActivitiesResponse = {
  ok: true;
  items: WellnessActivity[];
};

type DietPlan = {
  id: string;
  title: string;
  description: string;
  coverImageUrl?: string;
  waterTargetLiters?: number;
  mealType?: string;
  active: boolean;
  tags: string[];
};

type DietsResponse = {
  ok: true;
  items: DietPlan[];
};

async function fetchMeditations(): Promise<MeditationSession[]> {
  const res = await fetch("/api/numeroloji/meditations");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Meditasyonlar alınamadı.");
  }
  const data = json as MeditationsResponse;
  return Array.isArray(data?.items) ? data.items : [];
}

async function fetchActivities(): Promise<WellnessActivity[]> {
  const res = await fetch("/api/numeroloji/activities");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Aktiviteler alınamadı.");
  }
  const data = json as ActivitiesResponse;
  return Array.isArray(data?.items) ? data.items : [];
}

async function fetchDiets(): Promise<DietPlan[]> {
  const res = await fetch("/api/numeroloji/diets");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Diyetler alınamadı.");
  }
  const data = json as DietsResponse;
  return Array.isArray(data?.items) ? data.items : [];
}

function normalizeTag(value: string) {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, "")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u")
    .replace(/[^0-9a-z]+/g, "");
}

function getKarmicDebtTag(code: number) {
  return `karmikborc${code}`;
}

const GENERAL_TAG_KEYS = ["genel", "general"].map(normalizeTag);

function formatSeconds(totalSeconds: number) {
  const sec = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function mapCircleTaskPointsToXp(points: number) {
  const numeric = Math.max(0, Math.round(Number(points) || 0));
  if (numeric >= 3) return 50;
  if (numeric >= 2) return 30;
  return 15;
}

export function CircleTasksCard(props: { circleId: string; canManage: boolean; circleName?: string | null }) {
  const { circleId, canManage, circleName } = props;
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useSupabaseSession();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [kind, setKind] = useState<CircleTaskKind>("ozel");
  const [points, setPoints] = useState(1);
  const [busy, setBusy] = useState(false);

  const tasksQuery = useQuery({
    queryKey: ["circle-tasks", circleId, canManage],
    enabled: Boolean(circleId),
    queryFn: () => (canManage ? listCircleTasksForMe(circleId, undefined, true) : listCircleTasksForMe(circleId)),
    staleTime: 5_000,
  });

  const tasks = tasksQuery.data ?? [];

  const circleQuery = useQuery({
    queryKey: ["circle", circleId],
    enabled: Boolean(circleId),
    queryFn: () => fetchCircleById(circleId),
    staleTime: 10_000,
  });

  const wantedTagKeys = useMemo(() => {
    const rules = circleQuery.data?.rules?.all ?? [];
    const karmic = rules.filter((r) => r.key === "karmicDebt").map((r) => Math.trunc(Number(r.value))).filter((n) => Number.isFinite(n));
    const keys = karmic.map((n) => normalizeTag(getKarmicDebtTag(n)));
    return keys.length ? keys : GENERAL_TAG_KEYS;
  }, [circleQuery.data?.rules]);

  const meditationsQuery = useQuery({
    queryKey: ["panel-meditations"],
    queryFn: fetchMeditations,
    staleTime: 30_000,
  });

  const activitiesQuery = useQuery({
    queryKey: ["panel-activities"],
    queryFn: fetchActivities,
    staleTime: 30_000,
  });

  const dietsQuery = useQuery({
    queryKey: ["panel-diets"],
    queryFn: fetchDiets,
    staleTime: 30_000,
  });

  const recommendedMeditation = useMemo(() => {
    const items = (meditationsQuery.data ?? []).filter((m) => m && m.active);
    if (!items.length) return null;

    for (const m of items) {
      const keys = new Set((m.tags ?? []).filter(Boolean).map(normalizeTag));
      if (wantedTagKeys.some((k) => keys.has(k))) return m;
    }

    return items[0] ?? null;
  }, [meditationsQuery.data, wantedTagKeys]);

  const recommendedBreath = useMemo(() => {
    const items = (activitiesQuery.data ?? []).filter((a) => a && a.active);
    if (!items.length) return null;

    const scored = items
      .map((a) => {
        const keys = new Set((a.tags ?? []).filter(Boolean).map(normalizeTag));
        const matchesRule = wantedTagKeys.some((k) => keys.has(k));
        if (!matchesRule) return null;
        const hay = `${a.title} ${a.description} ${(a.tags ?? []).join(" ")}`.toLocaleLowerCase("tr-TR");
        const isBreath = hay.includes("nefes") || hay.includes("breath");
        return { a, isBreath };
      })
      .filter(Boolean) as Array<{ a: WellnessActivity; isBreath: boolean }>;

    const firstBreath = scored.find((x) => x.isBreath)?.a;
    return firstBreath ?? scored[0]?.a ?? null;
  }, [activitiesQuery.data, wantedTagKeys]);

  const recommendedDiet = useMemo(() => {
    const items = (dietsQuery.data ?? []).filter((d) => d && d.active);
    if (!items.length) return null;

    for (const d of items) {
      const keys = new Set((d.tags ?? []).filter(Boolean).map(normalizeTag));
      if (wantedTagKeys.some((k) => keys.has(k))) return d;
    }

    return items[0] ?? null;
  }, [dietsQuery.data, wantedTagKeys]);

  const [timedTaskId, setTimedTaskId] = useState<string | null>(null);
  const [timedTaskKind, setTimedTaskKind] = useState<"meditasyon" | "nefes" | null>(null);
  const [timedRemainingSec, setTimedRemainingSec] = useState(0);
  const timedEndsAtRef = useRef<number>(0);
  const timedIntervalRef = useRef<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timedAudioUrlRef = useRef<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editKind, setEditKind] = useState<CircleTaskKind>("ozel");
  const [editPoints, setEditPoints] = useState(1);
  const [editActive, setEditActive] = useState(true);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ["circle-tasks", circleId] });
    await queryClient.invalidateQueries({ queryKey: ["circle-task-counts", circleId] });
  };

  const reportCircleTaskProgress = async (task: { id: string; title: string; kind: string; points: number }, dateKey: string) => {
    const taskXp = mapCircleTaskPointsToXp(task.points);
    await sendProgressEvent({
      eventType: "task_completed",
      dateKey,
      sourceId: `circle:${circleId}:${task.id}:${dateKey}`,
      metadata: {
        source: "circle_task",
        circleId,
        taskId: task.id,
        taskKind: task.kind,
        title: task.title,
        taskPoints: taskXp,
        rawTaskPoints: task.points,
      },
    }).catch(() => undefined);

    await queryClient.invalidateQueries({ queryKey: ["progress-summary", user?.id] });
    await queryClient.invalidateQueries({ queryKey: ["progress-timeline", user?.id] });
    await queryClient.invalidateQueries({ queryKey: ["progress-timeline-practices", user?.id] });
  };

  const stopTimedRun = () => {
    if (timedIntervalRef.current) {
      window.clearInterval(timedIntervalRef.current);
      timedIntervalRef.current = null;
    }
    audioRef.current?.pause();
    setTimedTaskId(null);
    setTimedTaskKind(null);
    setTimedRemainingSec(0);
    timedEndsAtRef.current = 0;
    timedAudioUrlRef.current = null;
  };

  const startTimedForTask = (args: { taskId: string; dateKey: string; kind: "meditasyon" | "nefes"; durationMinutes: number; audioUrl?: string | null }) => {
    const durationMinutes = Number(args.durationMinutes) || 10;
    const durationSec = Math.max(60, Math.floor(durationMinutes * 60));

    stopTimedRun();

    setTimedTaskId(args.taskId);
    setTimedTaskKind(args.kind);
    setTimedRemainingSec(durationSec);
    timedEndsAtRef.current = Date.now() + durationSec * 1000;
    timedAudioUrlRef.current = args.audioUrl ?? null;

    // Start audio if available (timer still works even if missing).
    window.setTimeout(() => {
      if (!args.audioUrl) return;
      audioRef.current?.play().catch(() => {
        // autoplay might be blocked; timer continues
      });
    }, 50);

    timedIntervalRef.current = window.setInterval(() => {
      const nextRemaining = Math.max(0, Math.ceil((timedEndsAtRef.current - Date.now()) / 1000));
      setTimedRemainingSec(nextRemaining);

      if (nextRemaining <= 0) {
        if (timedIntervalRef.current) {
          window.clearInterval(timedIntervalRef.current);
          timedIntervalRef.current = null;
        }

        void (async () => {
          const t = tasks.find((x) => x.id === args.taskId);
          if (!t || t.completed) {
            stopTimedRun();
            return;
          }
          try {
            setBusy(true);
            const completion = await toggleCircleTaskCompletionInDb({ circleId, taskId: args.taskId, dateKey: args.dateKey });
            if (completion.completed) {
              await reportCircleTaskProgress({
                id: t.id,
                title: t.title,
                kind: t.kind,
                points: t.points,
              }, args.dateKey);
            }
            await invalidate();
          } catch (err) {
            const msg = err instanceof Error ? err.message : "İşlem başarısız";
            toast({ title: "Kaydedilemedi", description: msg, variant: "destructive" });
          } finally {
            setBusy(false);
            stopTimedRun();
          }
        })();
      }
    }, 1_000);
  };

  useEffect(() => {
    // Cleanup interval on unmount
    return () => {
      if (timedIntervalRef.current) window.clearInterval(timedIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    // If tasks refresh and the running task disappears/completes, stop.
    if (!timedTaskId) return;
    const t = tasks.find((x) => x.id === timedTaskId);
    if (!t || t.completed) stopTimedRun();
  }, [timedTaskId, tasks]);

  const onToggle = async (taskId: string, dateKey: string) => {
    const task = tasks.find((t) => t.id === taskId);
    setBusy(true);
    try {
      const completion = await toggleCircleTaskCompletionInDb({ circleId, taskId, dateKey });
      if (completion.completed && task) {
        await reportCircleTaskProgress({
          id: task.id,
          title: task.title,
          kind: task.kind,
          points: task.points,
        }, dateKey);
      }
      await invalidate();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "İşlem başarısız";
      toast({ title: "Kaydedilemedi", description: msg, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const onAdd = async () => {
    const t = title.trim();
    if (!t) return;

    setBusy(true);
    try {
      await createCircleTaskInDb({
        circleId,
        title: t,
        description: description.trim() || null,
        kind,
        points,
        sortOrder: tasks.length,
        active: true,
      });
      setTitle("");
      setDescription("");
      setKind("ozel");
      setPoints(1);
      toast({ title: "Eklendi", description: "Görev oluşturuldu." });
      await invalidate();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "İşlem başarısız";
      toast({ title: "Kaydedilemedi", description: msg, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (taskId: string) => {
    if (!canManage) return;
    const ok = window.confirm("Bu görevi silmek istediğine emin misin?");
    if (!ok) return;

    setBusy(true);
    try {
      await deleteCircleTaskInDb({ circleId, taskId });
      await invalidate();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "İşlem başarısız";
      toast({ title: "Kaydedilemedi", description: msg, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const presets: Array<{ title: string; kind: Exclude<CircleTaskKind, "ozel">; description: string; points: number }> = [
    {
      title: "Meditasyon Aktivasyonu",
      kind: "meditasyon",
      description: "Bugün seçilen meditasyonu yap ve deneyimini grupta paylaş.",
      points: 2,
    },
    {
      title: "Nefes Çalışması",
      kind: "nefes",
      description: "En az 5 dakika nefes çalışması uygula.",
      points: 1,
    },
    {
      title: "Ritüel Uygulaması",
      kind: "rituel",
      description: "Çemberin ritüelini uygula (niyet, yazma, şükran vb.).",
      points: 2,
    },
    {
      title: "Paylaşım (Sohbet)",
      kind: "sohbet",
      description: "Günün farkındalığını çember sohbetinde paylaş.",
      points: 1,
    },
  ];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-bold text-slate-900 dark:text-white">
            {circleName ? `${circleName} • Günün Görevleri` : "Günün Görevleri"}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">Her gün sıfırlanır</div>
        </div>
      </div>

      <div className="mt-3 space-y-2">
        {tasksQuery.isLoading ? (
          <div className="text-sm text-slate-500 dark:text-slate-400">Yükleniyor...</div>
        ) : tasks.length ? (
          tasks.map((task, idx) => {
            const isEditing = editingId === task.id;
            const isTimedTask =
              (task.kind === "meditasyon" && Boolean(recommendedMeditation)) || (task.kind === "nefes" && Boolean(recommendedBreath));
            const isTimedRunning = timedTaskId === task.id;

            return (
              <div key={task.id} className="rounded-xl border border-slate-100 p-3 dark:border-slate-800">
                <div className="flex items-start justify-between gap-3">
                  <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-3">
                    {isTimedTask ? (
                      <Checkbox checked={task.completed} disabled />
                    ) : (
                      <Checkbox checked={task.completed} onCheckedChange={() => void onToggle(task.id, task.dateKey)} disabled={busy} />
                    )}
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <div
                          className={cn(
                            "truncate text-sm font-semibold",
                            task.completed ? "text-slate-500 line-through dark:text-slate-400" : "text-slate-900 dark:text-white",
                          )}
                        >
                          {task.title}
                        </div>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-600 dark:bg-slate-900 dark:text-slate-300">
                          {task.kind}
                        </span>
                        <span className="rounded-full bg-[var(--panel-primary)]/10 px-2 py-0.5 text-[10px] font-bold uppercase text-[var(--panel-primary)]">
                          {task.points} puan
                        </span>
                        {!task.active ? (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                            Pasif
                          </span>
                        ) : null}
                      </div>
                      {task.description ? <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{task.description}</div> : null}

                      {task.kind === "meditasyon" ? (
                        recommendedMeditation ? (
                          <div className="mt-3 rounded-xl bg-slate-50 p-3 text-xs text-slate-700 dark:bg-slate-900/40 dark:text-slate-200">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="font-bold">
                                Önerilen meditasyon: <span className="font-extrabold">{recommendedMeditation.title}</span>
                              </div>
                              <div className="font-semibold text-slate-500 dark:text-slate-400">{recommendedMeditation.durationMinutes} dk</div>
                            </div>

                            {recommendedMeditation.audioUrl ? (
                              isTimedRunning ? (
                                <div className="mt-3">
                                  <audio
                                    ref={audioRef}
                                    src={timedAudioUrlRef.current ?? recommendedMeditation.audioUrl}
                                    controls
                                    className="h-10 w-full"
                                  />
                                </div>
                              ) : (
                                <div className="mt-2 text-slate-500 dark:text-slate-400">Başlatınca ses oynatılacaktır.</div>
                              )
                            ) : (
                              <div className="mt-2 text-slate-500 dark:text-slate-400">Ses kaydı bulunamadı; süre takibi ile tamamlanır.</div>
                            )}

                            {!task.completed ? (
                              <div className="mt-3 flex flex-wrap items-center gap-2">
                                {isTimedRunning ? (
                                  <>
                                    <Button type="button" variant="outline" size="sm" className="rounded-lg" onClick={stopTimedRun} disabled={busy}>
                                      Durdur
                                    </Button>
                                    <div className="rounded-lg bg-white px-3 py-2 font-mono text-sm font-bold text-slate-900 shadow-sm dark:bg-slate-950 dark:text-white">
                                      {formatSeconds(timedRemainingSec)}
                                    </div>
                                  </>
                                ) : (
                                  <Button
                                    type="button"
                                    size="sm"
                                    className="rounded-lg"
                                    disabled={busy || !recommendedMeditation}
                                    onClick={() =>
                                      startTimedForTask({
                                        taskId: task.id,
                                        dateKey: task.dateKey,
                                        kind: "meditasyon",
                                        durationMinutes: recommendedMeditation.durationMinutes,
                                        audioUrl: recommendedMeditation.audioUrl ?? null,
                                      })
                                    }
                                  >
                                    Başlat
                                  </Button>
                                )}
                                <div className="text-[11px] text-slate-500 dark:text-slate-400">Süre dolunca otomatik tamamlanır.</div>
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <div className="mt-3 rounded-xl bg-slate-50 p-3 text-xs text-slate-600 dark:bg-slate-900/40 dark:text-slate-300">
                            Bu çemberin kurallarına uygun meditasyon bulunamadı. <Link className="font-bold text-[var(--panel-primary)] hover:underline" to="/panel/meditasyon">
                              Meditasyonlara git
                            </Link>
                            .
                          </div>
                        )
                      ) : task.kind === "nefes" ? (
                        recommendedBreath ? (
                          <div className="mt-3 rounded-xl bg-slate-50 p-3 text-xs text-slate-700 dark:bg-slate-900/40 dark:text-slate-200">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="font-bold">
                                Önerilen nefes çalışması: <span className="font-extrabold">{recommendedBreath.title}</span>
                              </div>
                              <div className="font-semibold text-slate-500 dark:text-slate-400">{recommendedBreath.durationMinutes ?? 5} dk</div>
                            </div>

                            <div className="mt-2 text-slate-500 dark:text-slate-400">{recommendedBreath.description}</div>

                            {!task.completed ? (
                              <div className="mt-3 flex flex-wrap items-center gap-2">
                                {isTimedRunning ? (
                                  <>
                                    <Button type="button" variant="outline" size="sm" className="rounded-lg" onClick={stopTimedRun} disabled={busy}>
                                      Durdur
                                    </Button>
                                    <div className="rounded-lg bg-white px-3 py-2 font-mono text-sm font-bold text-slate-900 shadow-sm dark:bg-slate-950 dark:text-white">
                                      {formatSeconds(timedRemainingSec)}
                                    </div>
                                  </>
                                ) : (
                                  <Button
                                    type="button"
                                    size="sm"
                                    className="rounded-lg"
                                    disabled={busy || !recommendedBreath}
                                    onClick={() =>
                                      startTimedForTask({
                                        taskId: task.id,
                                        dateKey: task.dateKey,
                                        kind: "nefes",
                                        durationMinutes: recommendedBreath.durationMinutes ?? 5,
                                        audioUrl: null,
                                      })
                                    }
                                  >
                                    Başlat
                                  </Button>
                                )}
                                <div className="text-[11px] text-slate-500 dark:text-slate-400">Süre dolunca otomatik tamamlanır.</div>
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <div className="mt-3 rounded-xl bg-slate-50 p-3 text-xs text-slate-600 dark:bg-slate-900/40 dark:text-slate-300">
                            Bu çemberin kurallarına uygun nefes çalışması bulunamadı.
                          </div>
                        )
                      ) : task.kind === "diyet" ? (
                        recommendedDiet ? (
                          <div className="mt-3 rounded-xl bg-slate-50 p-3 text-xs text-slate-700 dark:bg-slate-900/40 dark:text-slate-200">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="font-bold">
                                Önerilen diyet planı: <span className="font-extrabold">{recommendedDiet.title}</span>
                              </div>
                            </div>

                            <div className="mt-2 text-slate-500 dark:text-slate-400">{recommendedDiet.description}</div>

                            <div className="mt-3 flex flex-wrap items-center gap-2">
                              {typeof recommendedDiet.waterTargetLiters === "number" ? (
                                <span className="rounded-full bg-white px-3 py-1 text-[11px] font-bold text-slate-700 shadow-sm dark:bg-slate-950 dark:text-slate-200">
                                  Su: {recommendedDiet.waterTargetLiters} L
                                </span>
                              ) : null}
                              {recommendedDiet.mealType ? (
                                <span className="rounded-full bg-white px-3 py-1 text-[11px] font-bold text-slate-700 shadow-sm dark:bg-slate-950 dark:text-slate-200">
                                  {recommendedDiet.mealType}
                                </span>
                              ) : null}
                              <Link to="/panel/diyet" className="text-[11px] font-bold text-[var(--panel-primary)] hover:underline">
                                Diyet sayfasını aç
                              </Link>
                            </div>

                            <div className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">Diyet görevi tıklayarak tamamlanır.</div>
                          </div>
                        ) : (
                          <div className="mt-3 rounded-xl bg-slate-50 p-3 text-xs text-slate-600 dark:bg-slate-900/40 dark:text-slate-300">
                            Bu çemberin kurallarına uygun diyet planı bulunamadı. <Link className="font-bold text-[var(--panel-primary)] hover:underline" to="/panel/diyet">
                              Diyetlere git
                            </Link>
                            .
                          </div>
                        )
                      ) : null}
                    </div>
                  </label>

                  {canManage ? (
                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-lg"
                        disabled={busy || idx === 0}
                        onClick={async () => {
                          const prev = tasks[idx - 1];
                          if (!prev) return;
                          setBusy(true);
                          try {
                            await updateCircleTaskInDb({ circleId, taskId: task.id, sortOrder: prev.sortOrder });
                            await updateCircleTaskInDb({ circleId, taskId: prev.id, sortOrder: task.sortOrder });
                            await invalidate();
                          } catch (err) {
                            const msg = err instanceof Error ? err.message : "İşlem başarısız";
                            toast({ title: "Kaydedilemedi", description: msg, variant: "destructive" });
                          } finally {
                            setBusy(false);
                          }
                        }}
                      >
                        ↑
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-lg"
                        disabled={busy || idx === tasks.length - 1}
                        onClick={async () => {
                          const next = tasks[idx + 1];
                          if (!next) return;
                          setBusy(true);
                          try {
                            await updateCircleTaskInDb({ circleId, taskId: task.id, sortOrder: next.sortOrder });
                            await updateCircleTaskInDb({ circleId, taskId: next.id, sortOrder: task.sortOrder });
                            await invalidate();
                          } catch (err) {
                            const msg = err instanceof Error ? err.message : "İşlem başarısız";
                            toast({ title: "Kaydedilemedi", description: msg, variant: "destructive" });
                          } finally {
                            setBusy(false);
                          }
                        }}
                      >
                        ↓
                      </Button>

                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-lg"
                        disabled={busy}
                        onClick={() => {
                          if (isEditing) {
                            setEditingId(null);
                            return;
                          }
                          setEditingId(task.id);
                          setEditTitle(task.title);
                          setEditDescription(task.description ?? "");
                          setEditKind(task.kind);
                          setEditPoints(task.points);
                          setEditActive(task.active);
                        }}
                      >
                        {isEditing ? "Kapat" : "Düzenle"}
                      </Button>

                      <Button size="sm" variant="destructive" className="rounded-lg" onClick={() => void onDelete(task.id)} disabled={busy}>
                        Sil
                      </Button>
                    </div>
                  ) : null}
                </div>

                {canManage && isEditing ? (
                  <div className="mt-4 space-y-3 rounded-xl bg-slate-50 p-4 dark:bg-slate-900/30">
                    <div className="grid gap-3">
                      <div className="space-y-2">
                        <Label>Görev adı</Label>
                        <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="h-10 rounded-xl" />
                      </div>
                      <div className="space-y-2">
                        <Label>Açıklama</Label>
                        <Textarea value={editDescription} onChange={(e) => setEditDescription(e.target.value)} rows={2} />
                      </div>
                      <div className="grid gap-3 md:grid-cols-3">
                        <div className="space-y-2">
                          <Label>Tür</Label>
                          <Select value={editKind} onValueChange={(v) => setEditKind(v as CircleTaskKind)}>
                            <SelectTrigger className="rounded-xl">
                              <SelectValue placeholder="Tür" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="meditasyon">Meditasyon</SelectItem>
                              <SelectItem value="nefes">Nefes</SelectItem>
                              <SelectItem value="diyet">Diyet</SelectItem>
                              <SelectItem value="rituel">Ritüel</SelectItem>
                              <SelectItem value="sohbet">Sohbet</SelectItem>
                              <SelectItem value="ozel">Özel</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Puan</Label>
                          <Input
                            value={String(editPoints)}
                            onChange={(e) => setEditPoints(Math.max(0, Math.min(100, Math.trunc(Number(e.target.value) || 0))))}
                            className="h-10 rounded-xl"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Durum</Label>
                          <label className="flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm dark:border-slate-800 dark:bg-slate-950">
                            <Checkbox checked={editActive} onCheckedChange={(v) => setEditActive(Boolean(v))} />
                            Aktif
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        className="rounded-xl"
                        onClick={() => {
                          setEditingId(null);
                        }}
                      >
                        Vazgeç
                      </Button>
                      <Button
                        className="rounded-xl"
                        disabled={busy}
                        onClick={async () => {
                          setBusy(true);
                          try {
                            await updateCircleTaskInDb({
                              circleId,
                              taskId: task.id,
                              title: editTitle,
                              description: editDescription,
                              kind: editKind,
                              points: editPoints,
                              active: editActive,
                            });
                            setEditingId(null);
                            await invalidate();
                            toast({ title: "Kaydedildi" });
                          } catch (err) {
                            const msg = err instanceof Error ? err.message : "İşlem başarısız";
                            toast({ title: "Kaydedilemedi", description: msg, variant: "destructive" });
                          } finally {
                            setBusy(false);
                          }
                        }}
                      >
                        Kaydet
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })
        ) : (
          <div className="text-sm text-slate-500 dark:text-slate-400">Henüz görev eklenmemiş.</div>
        )}
      </div>

      {canManage ? (
        <div className="mt-4 space-y-3">
          <div className="space-y-2">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Görev adı (örn: Kalp çakra meditasyonu)"
              className="h-10 rounded-xl"
              disabled={busy}
            />
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Görev açıklaması (ne yapılacak?)"
              className="h-10 rounded-xl"
              disabled={busy}
            />
            <div className="grid gap-2 sm:grid-cols-2">
              <Input
                value={kind}
                onChange={(e) => setKind(e.target.value as CircleTaskKind)}
                placeholder="Tür: meditasyon / nefes / rituel / sohbet / ozel"
                className="h-10 rounded-xl"
                disabled={busy}
              />
              <Input
                value={String(points)}
                onChange={(e) => setPoints(Math.max(0, Math.min(100, Math.trunc(Number(e.target.value) || 0))))}
                placeholder="Puan (örn: 2)"
                className="h-10 rounded-xl"
                disabled={busy}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" className="rounded-xl" type="button" onClick={() => void onAdd()} disabled={busy}>
              Ekle
            </Button>
            {presets.map((p) => (
              <Button
                key={p.title}
                variant="secondary"
                className="rounded-xl"
                type="button"
                onClick={() => {
                  setTitle(p.title);
                  setDescription(p.description);
                  setKind(p.kind);
                  setPoints(p.points);
                }}
                disabled={busy}
              >
                {p.title}
              </Button>
            ))}
            <Button className="rounded-xl" type="button" onClick={() => void onAdd()} disabled={busy || !title.trim()}>
              Kaydet
            </Button>
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Yönetici bu görevleri sonradan ekleyebilir/silebilir. Üyeler her gün tamamladıkça puan kazanır.
          </div>
        </div>
      ) : null}
    </div>
  );
}
